<script setup lang="ts">
import type { Component } from "vue";

const props = defineProps<{
  label: string;
  icon: Component;
  active: boolean;
  opacity: number;
}>();

const emit = defineEmits<{
  toggle: [];
  "update:opacity": [value: number];
}>();

/** Emit the selected layer opacity from the native range control. */
function updateOpacity(event: Event) {
  emit("update:opacity", Number((event.target as HTMLInputElement).value));
}
</script>

<template>
  <div class="flex items-center gap-2">
    <component
      :is="icon"
      class="h-4 w-4 shrink-0"
      :class="active ? 'text-blue-400' : 'text-text-muted'"
      aria-hidden="true"
    />
    <span
      class="min-w-10 text-xs font-medium whitespace-nowrap text-text-primary"
    >
      {{ label }}
    </span>
    <button
      class="relative h-[18px] w-8 shrink-0 cursor-pointer rounded-small border-0 p-0 transition-colors duration-200 ease-[ease]"
      :class="active ? 'bg-blue-500' : 'bg-app-selected'"
      type="button"
      role="switch"
      :aria-checked="active"
      :aria-label="`${label} layer`"
      @click="emit('toggle')"
    >
      <span
        class="absolute top-0.5 left-0.5 size-3.5 rounded-full bg-white transition-transform duration-200 ease-[ease]"
        :class="{ 'translate-x-3.5': active }"
      />
    </button>
    <input
      class="layer-menu-slider h-1 w-25 cursor-pointer appearance-none rounded-xs bg-app-selected outline-none disabled:cursor-not-allowed disabled:opacity-30"
      type="range"
      min="0"
      max="1"
      step="0.05"
      :value="props.opacity"
      :disabled="!active"
      :aria-label="`${label} opacity`"
      @input="updateOpacity"
    />
  </div>
</template>

<!-- Browser-specific range thumbs have no shared native pseudo-element. -->
<style scoped>
.layer-menu-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.layer-menu-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}
</style>
